import { useState, useEffect } from 'react';
import { Box, Typography, Alert, Snackbar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import CampaignDetailsSection from '../components/campaign/CampaignDetailsSection';
import ReviewDestinationsSection, { 
  type PlatformDisplay, 
  type PlatformLink 
} from '../components/campaign/ReviewDestinationsSection';
import CustomerRewardsSection from '../components/campaign/CustomerRewardsSection';
import CampaignActionButtons from '../components/campaign/CampaignActionButtons';
import { getFullUserData, getUserBusinessName } from '../utils/userHelper';
import { RewardType, Currency } from '../types/campaign';
import { createCampaignFromForm, updateCampaign } from '../services/campaignService';

const PageContainer = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  bgcolor: '#F9FAFB'
});

const MainContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

const ContentArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '1200px',
  margin: '0 auto',
  width: '100%'
}));

const platforms: PlatformDisplay[] = [
  {
    id: 'google',
    name: 'Google',
    logo: 'https://lh3.googleusercontent.com/aida-public/AB6AXuBROcZO4F2WmyE4VN0v7bxNNzPLBBVKYbqSLfdjy1tRZMi2L9bFYbPnvjgS5xxqKgKptVTPoHiGJmYmksIgpUuT-pZlqoVnux_VukBXlLv_WfLxNHZsuFVtKLYtmJHByh8WLS6pzlWKN2EpT_qkTX8c29iosDjPnYFZ_xNDG5AtVNUJeauXzPShpG4oeSntXH-ha-LMnQmVqIbg2e_s3X0K8bxogNL8FfoPGE0uz-GK8hmzJ6_5PrjHQJiT8pM4Jh47SI27SU3qZdw'
  },
  {
    id: 'facebook',
    name: 'Facebook',
    logo: 'https://lh3.googleusercontent.com/aida-public/AB6AXuAqIODW-ZP-8VAG3r2Wp1nIYmg9jhpuaGH8NAk2YkDup_ebH6zdG8FqOG80zwDhYO7uN-H2v2MuhNq2Ufbw0h4iSow4SRIizSLnskC_StTSkCDpwLxM08lv8cOD7L77ApPVUBhyk0zpicYoWI3rOgb0vhyXGBrVJ4V8MTuPYWJwz-IPf7YmeBF5-DzsbdYdXy-dJUATrklcLIvtzsUVnv1TgLymu_4uJF_FapZcwKkbsTuoiReDyPRApXxKoLNIscuI3ieTi4SRTgM'
  },
  {
    id: 'yelp',
    name: 'Yelp',
    logo: 'https://lh3.googleusercontent.com/aida-public/AB6AXuDJLO7BWhRwl9u3E-mP4ai_nEQSlEpFxx-bjFMeuQImFzq4yMi5Y4ZffDUE8okUT37HdwXfmRxrymb8g1oLO_MpbpSl9wL37yqmX42bfH4MpsUu8bFMUOKB85TSaKX6e1_BpXZXO2UkfOwf9NaL4QoZ6NT6uaGBiMcBihXyqEhen9CGElKAgEq-Kw3izPmKL87uNfz5-nrhOUppvJT-prWbvIFRdhm2fQvwPOyVxI5eBib3JSH9Kv7xVueNZEDuSGJctjt3LvTv1FE'
  },
  {
    id: 'custom',
    name: 'Custom',
    logo: 'https://img.icons8.com/fluency/48/globe.png' // Generic globe icon for custom URLs
  }
];

const CreateCampaign = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = getFullUserData();
  const businessName = getUserBusinessName();
  
  // Check if we're in edit mode
  const { isEditMode, campaignId, campaignData } = location.state || {};
  
  // Campaign details state
  const [campaignName, setCampaignName] = useState('');
  
  // Platform selection and URLs state
  const [platformLinks, setPlatformLinks] = useState<PlatformLink[]>([]);
  const [selectedPlatformId, setSelectedPlatformId] = useState<string>('');
  
  // Reward type state
  const [rewardType, setRewardType] = useState<RewardType>('coupon' as RewardType);
  
  // Coupon state
  const [discountAmount, setDiscountAmount] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [couponTerms, setCouponTerms] = useState('');
  
  // Cash payout state
  const [cashAmount, setCashAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [cashTerms, setCashTerms] = useState('');

  // UI states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pre-fill form when in edit mode
  useEffect(() => {
    if (isEditMode && campaignData) {
      console.log('🔄 Loading campaign data for edit:', campaignData);
      
      // Pre-fill basic details
      if (campaignData.campaign_name) setCampaignName(campaignData.campaign_name);
      
      // Pre-fill platform links
      if (campaignData.platform && campaignData.platform_link) {
        const platform: PlatformLink = {
          id: campaignData.platform.toLowerCase(),
          platform: campaignData.platform.toLowerCase(),
          url: campaignData.platform_link
        };
        setPlatformLinks([platform]);
        setSelectedPlatformId(campaignData.platform.toLowerCase());
      }
      
      // Pre-fill reward type and details
      if (campaignData.reward_type) {
        setRewardType(campaignData.reward_type as RewardType);
        
        if (campaignData.reward_type === 'coupon') {
          if (campaignData.coupon_discount_percent) setDiscountAmount(campaignData.coupon_discount_percent.toString());
          if (campaignData.coupon_prefix) setCouponCode(campaignData.coupon_prefix);
          if (campaignData.coupon_expiry_date) setExpirationDate(campaignData.coupon_expiry_date.split('T')[0]); // Format for date input
          if (campaignData.coupon_terms) setCouponTerms(campaignData.coupon_terms);
        } else if (campaignData.reward_type === 'cash') {
          if (campaignData.cash_amount) setCashAmount(campaignData.cash_amount.toString());
          if (campaignData.payment_integrator) setPaymentMethod(campaignData.payment_integrator);
        }
      }
      
      console.log('✅ Campaign data loaded for editing');
    }
  }, [isEditMode, campaignData]);

  const handlePlatformToggle = (platformId: string) => {
    const existingPlatform = platformLinks.find(p => p.id === platformId);
    
    if (existingPlatform) {
      // Remove platform
      setPlatformLinks(prev => prev.filter(p => p.id !== platformId));
      if (selectedPlatformId === platformId) {
        setSelectedPlatformId('');
      }
    } else {
      // Add platform with empty URL
      const newPlatform: PlatformLink = {
        id: platformId,
        platform: platformId,
        url: ''
      };
      setPlatformLinks(prev => [...prev, newPlatform]);
      // Auto-select if it's the first platform
      if (platformLinks.length === 0) {
        setSelectedPlatformId(platformId);
      }
    }
  };

  const handlePlatformUrlChange = (platformId: string, url: string) => {
    setPlatformLinks(prev =>
      prev.map(p => p.id === platformId ? { ...p, url } : p)
    );
  };

  const handlePlatformSelect = (platformId: string) => {
    setSelectedPlatformId(platformId);
  };

  const handleRewardTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newRewardType: RewardType | null
  ) => {
    // Prevent selecting cash reward (coming soon)
    if (newRewardType === 'cash') {
      return;
    }
    if (newRewardType !== null) {
      setRewardType(newRewardType);
    }
  };

  const handleAddCustomLink = () => {
    // Generate unique ID for custom platform
    const customId = `custom-${Date.now()}`;
    const newPlatform: PlatformLink = {
      id: customId,
      platform: 'custom',
      url: ''
    };
    setPlatformLinks(prev => [...prev, newPlatform]);
    // Auto-select if it's the first platform
    if (platformLinks.length === 0) {
      setSelectedPlatformId(customId);
    }
  };

  const handleRemovePlatform = (platformId: string) => {
    setPlatformLinks(prev => prev.filter(p => p.id !== platformId));
    if (selectedPlatformId === platformId) {
      // Select the first remaining platform if any
      const remaining = platformLinks.filter(p => p.id !== platformId);
      setSelectedPlatformId(remaining.length > 0 ? remaining[0].id : '');
    }
  };

  const handleSaveAndActivate = async () => {
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      // Validation
      if (!campaignName.trim()) {
        throw new Error('Campaign name is required');
      }

      if (platformLinks.length === 0) {
        throw new Error('Please add at least one platform');
      }

      if (!selectedPlatformId) {
        throw new Error('Please select a platform to use for this campaign');
      }

      // Get the selected platform
      const selectedPlatform = platformLinks.find(p => p.id === selectedPlatformId);
      
      if (!selectedPlatform) {
        throw new Error('Selected platform not found');
      }

      if (!selectedPlatform.url.trim()) {
        throw new Error(`Please enter a URL for ${selectedPlatform.platform === 'custom' ? 'the custom platform' : selectedPlatform.platform}`);
      }

      // Validate URL format
      try {
        new URL(selectedPlatform.url);
      } catch {
        throw new Error('Please enter a valid URL (e.g., https://example.com)');
      }

      if (isEditMode && !campaignId) {
        throw new Error('Campaign ID is required for update');
      }

      // Prepare API data
      const apiData: any = {
        campaign_name: campaignName,
        platform_link: selectedPlatform.url,
        reward_type: rewardType,
      };

      // Add reward-specific fields
      if (rewardType === 'coupon') {
        if (!discountAmount || !couponCode || !expirationDate) {
          throw new Error('Please fill in all coupon details');
        }

        // Convert expiration date to ISO 8601 format
        const expiryDateISO = new Date(expirationDate).toISOString();

        apiData.coupon_discount_percent = Number(discountAmount);
        apiData.coupon_expiry_date = expiryDateISO;
        apiData.coupon_terms = couponTerms || 'Standard terms and conditions apply.';
        apiData.coupon_prefix = couponCode.toUpperCase();
      } else if (rewardType === 'cash') {
        if (!cashAmount || !paymentMethod) {
          throw new Error('Please fill in all cash reward details');
        }

        apiData.cash_amount = Number(cashAmount);
        apiData.cash_currency = Currency.USD;
        apiData.payment_integrator = paymentMethod;
      }

      if (isEditMode) {
        // Update existing campaign
        console.log('Updating campaign with data:', apiData);
        const response = await updateCampaign(campaignId, apiData);

        console.log('Campaign updated successfully:', response.data?.campaign);

        // Navigate back to campaign details immediately
        navigate(`/campaigns/${campaignId}`, {
          state: {
            message: 'Campaign updated successfully!'
          }
        });
      } else {
        // Create new campaign - need to convert to formData format
        const formData: any = {
          campaignName,
          platform: selectedPlatform.platform,
          platformLink: selectedPlatform.url,
          rewardType: rewardType,
        };

        if (rewardType === 'coupon') {
          const expiryDateISO = new Date(expirationDate).toISOString();
          formData.couponDiscountPercent = Number(discountAmount);
          formData.couponExpiryDate = expiryDateISO;
          formData.couponTerms = couponTerms || 'Standard terms and conditions apply.';
          formData.couponPrefix = couponCode.toUpperCase();
        } else if (rewardType === 'cash') {
          formData.cashAmount = Number(cashAmount);
          formData.cashCurrency = Currency.USD;
          formData.paymentIntegrator = paymentMethod;
        }

        console.log('Creating campaign with data:', formData);
        const response = await createCampaignFromForm(formData);

        console.log('Campaign created successfully:', response.data?.campaign);
        setSuccess('Campaign created successfully!');

        // Navigate to next step (survey creation)
        navigate('/campaigns/create/survey', {
          state: { 
            campaignId: response.data?.campaign?.id,
            campaignData: response.data?.campaign 
          }
        });
      }

    } catch (err) {
      console.error(`${isEditMode ? 'Update' : 'Creation'} error:`, err);
      setError(err instanceof Error ? err.message : `Failed to ${isEditMode ? 'update' : 'create'} campaign. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  return (
    <PageContainer>
      <Sidebar user={userData} />
      
      <MainContent>
        <DashboardHeader
          businessName={businessName}
          userAvatar={userData.avatar}
          businessLogo={userData.businessLogo}
        />
        
        <ContentArea>
          {/* Page Heading */}
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: { xs: '1.875rem', md: '2.25rem' },
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                color: '#111827',
                mb: 0.5
              }}
            >
              {isEditMode ? 'Edit Campaign' : 'Create a New Review Campaign'}
            </Typography>
            <Typography
              sx={{
                color: '#6B7280',
                fontSize: '0.875rem',
                fontFamily: '"Inter", sans-serif'
              }}
            >
              Set up your campaign details, select review platforms, and configure customer rewards
            </Typography>
          </Box>

          {/* Campaign Details Section */}
          <CampaignDetailsSection
            campaignName={campaignName}
            onCampaignNameChange={setCampaignName}
          />

          {/* Review Destinations Section */}
          <ReviewDestinationsSection
            platforms={platforms}
            platformLinks={platformLinks}
            selectedPlatformId={selectedPlatformId}
            onPlatformToggle={handlePlatformToggle}
            onPlatformUrlChange={handlePlatformUrlChange}
            onPlatformSelect={handlePlatformSelect}
            onAddCustomLink={handleAddCustomLink}
            onRemovePlatform={handleRemovePlatform}
          />

          {/* Customer Rewards Section */}
          <CustomerRewardsSection
            rewardType={rewardType}
            onRewardTypeChange={handleRewardTypeChange}
            discountAmount={discountAmount}
            couponCode={couponCode}
            expirationDate={expirationDate}
            couponTerms={couponTerms}
            onDiscountAmountChange={setDiscountAmount}
            onCouponCodeChange={setCouponCode}
            onExpirationDateChange={setExpirationDate}
            onCouponTermsChange={setCouponTerms}
            cashAmount={cashAmount}
            paymentMethod={paymentMethod}
            cashTerms={cashTerms}
            onCashAmountChange={setCashAmount}
            onPaymentMethodChange={setPaymentMethod}
            onCashTermsChange={setCashTerms}
          />

          {/* Action Buttons */}
          <CampaignActionButtons
            onCancel={handleCancel}
            onSaveAndActivate={handleSaveAndActivate}
            isLoading={isLoading}
            isEditMode={isEditMode}
          />
        </ContentArea>
      </MainContent>

      {/* Success/Error Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default CreateCampaign;
